import { createTheme, darken, getContrastRatio, lighten } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

/**
 * Single source of truth for MUI theming. No other file calls `createTheme`.
 *
 * Values are the **Polaris design-system tokens** (DecisionPoint's internal
 * design system, per DDR-PLT-008), translated from the prototype's
 * `docs/design/claude-design-prototype/shared/Theme.jsx` (`MODE_TOKENS`, the
 * values the prototype actually *renders*) and `shared/colors_and_type.css`.
 * Edit tokens there first, mirror here.
 *
 * Light + dark are both defined as MUI **color schemes** (the prototype ships a
 * navy-tinted dark mode; `cssVariables` + `colorSchemeSelector: 'class'` let the
 * account menu toggle it via `useColorScheme()`). The navy operator chrome
 * (`palette.brand`) is defined per scheme so the sidebar reads it through the
 * theme rather than hard-coding hexes.
 *
 * **Per-customer branding.** {@link createAppTheme} layers the effective
 * customer's `BrandPrimaryColor` / `BrandHeaderColor` (from `whoami`) over the
 * Polaris defaults. `app/BrandedThemeProvider` calls it and rebuilds when the
 * effective customer changes (including on impersonation start/end, which
 * invalidates `['whoami']`). All theming still flows through THIS single
 * `createTheme` call — never a second ad-hoc theme object.
 *
 * Only `palette.primary` and the `palette.brand` sidebar chrome are branded.
 * The semantic ramps (success / error / warning / info) and the chart series
 * palette stay fixed: a customer's brand color must not change what "error" or
 * a chart series means. `brand.accent` (the orange active-nav marker) also
 * stays DecisionPoint's, since there is no customer token for it.
 */

/**
 * Inter is the app's primary typeface. The prototype kit hardcodes Inter for
 * ALL UI text (not just buttons), so to match its rendered look the body stack
 * leads with Inter too — the older token sheet's "system-ui body" note reflects
 * an intent the rendered prototype overrode. Loaded via the webfont link in
 * `index.html`; the system stack is the fallback until it lands.
 */
const FONT_UI = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const FONT_BODY =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/**
 * Navy operator chrome (sidebar). Values mirror `MODE_TOKENS.{light,dark}` in
 * the prototype `shared/Theme.jsx` — the rendered values, not the stale
 * `--color-brand-dark-bg` legacy CSS token. In dark mode the rail steps DEEPER
 * navy so it stays the darkest surface. `accent` is the vibrant-orange active
 * marker (the 3px left rail on the active nav row).
 */
export interface BrandPalette {
  sidebarBg: string;
  sidebarFg: string;
  sidebarFgMuted: string;
  sidebarHoverBg: string;
  sidebarActiveBg: string;
  sidebarActiveFg: string;
  sidebarDivider: string;
  sidebarBorder: string;
  sidebarTriggerHoverBg: string;
  /** Vibrant-orange active marker (the 3px left rail on the active nav row). */
  accent: string;
}

const brandLight: BrandPalette = {
  sidebarBg: '#003F75',
  sidebarFg: 'rgba(255, 255, 255, 0.92)',
  sidebarFgMuted: 'rgba(255, 255, 255, 0.65)',
  sidebarHoverBg: 'rgba(255, 255, 255, 0.06)',
  sidebarActiveBg: '#1A5991',
  sidebarActiveFg: '#FFFFFF',
  sidebarDivider: 'rgba(255, 255, 255, 0.10)',
  sidebarBorder: 'rgba(255, 255, 255, 0.08)',
  sidebarTriggerHoverBg: 'rgba(255, 255, 255, 0.08)',
  accent: '#F4770C',
};

const brandDark: BrandPalette = {
  sidebarBg: '#050F1F',
  sidebarFg: 'rgba(255, 255, 255, 0.92)',
  sidebarFgMuted: 'rgba(255, 255, 255, 0.65)',
  sidebarHoverBg: 'rgba(255, 255, 255, 0.05)',
  sidebarActiveBg: '#1A5991',
  sidebarActiveFg: '#FFFFFF',
  sidebarDivider: 'rgba(255, 255, 255, 0.08)',
  sidebarBorder: 'rgba(255, 255, 255, 0.06)',
  sidebarTriggerHoverBg: 'rgba(255, 255, 255, 0.06)',
  accent: '#F4770C',
};

/**
 * The per-customer branding inputs, as they arrive on `whoami.customer`. Both
 * are optional and may be null — a null/blank/malformed value falls back to the
 * Polaris default for that token, so a partially-branded customer is fine.
 */
export interface BrandingTokens {
  brandPrimaryColor?: string | null;
  brandHeaderColor?: string | null;
}

/**
 * Strict 6-digit hex. Mirrors the server-side guard
 * (`CustomerRequestValidation.HexColor`) so the SPA rejects exactly what the API
 * rejects; anything else is treated as "unset" rather than passed to MUI's color
 * manipulators, which throw on unparseable input.
 */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const normalizeHex = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  return HEX_RE.test(trimmed) ? trimmed : null;
};

/**
 * WCAG AA body-text threshold. Used to decide whether sidebar chrome sits on a
 * dark or light brand color; MUI's own `contrastThreshold` (3, the AA
 * large-text/UI ratio) governs `primary.contrastText` for button labels.
 */
const AA_CONTRAST = 4.5;

/** True when white text clears AA against `background`. */
const prefersLightForeground = (background: string) =>
  getContrastRatio(background, '#FFFFFF') >= AA_CONTRAST;

/**
 * Derives a full `primary` ramp from a customer brand color. `contrastText` is
 * chosen by measured contrast rather than assumed white — a customer who brands
 * with a pale color still gets readable button labels.
 */
function brandedPrimary(main: string) {
  return {
    main,
    light: lighten(main, 0.2),
    dark: darken(main, 0.2),
    contrastText: getContrastRatio(main, '#FFFFFF') >= 3 ? '#FFFFFF' : '#041D33',
  };
}

/**
 * Derives the sidebar chrome from a customer header color.
 *
 * The rail is normally deep navy with white text. A customer may pick any color,
 * so the foreground/hover/divider alphas are chosen from measured contrast: on a
 * dark header we keep the white-on-navy treatment; on a light header we flip to
 * dark ink so the nav never becomes unreadable. `activeBg` steps *away* from the
 * base in whichever direction stays distinguishable.
 *
 * `scheme: 'dark'` additionally deepens the base color, preserving the
 * prototype's rule that the rail is the darkest surface in dark mode.
 */
function brandedSidebar(headerColor: string, scheme: 'light' | 'dark'): BrandPalette {
  const base = scheme === 'dark' ? darken(headerColor, 0.45) : headerColor;
  const onDark = prefersLightForeground(base);
  const ink = onDark ? '255, 255, 255' : '4, 29, 51';

  return {
    sidebarBg: base,
    sidebarFg: `rgba(${ink}, 0.92)`,
    sidebarFgMuted: `rgba(${ink}, 0.65)`,
    sidebarHoverBg: `rgba(${ink}, ${scheme === 'dark' ? 0.05 : 0.06})`,
    sidebarActiveBg: onDark ? lighten(base, 0.18) : darken(base, 0.12),
    sidebarActiveFg: onDark ? '#FFFFFF' : '#041D33',
    sidebarDivider: `rgba(${ink}, ${scheme === 'dark' ? 0.08 : 0.1})`,
    sidebarBorder: `rgba(${ink}, ${scheme === 'dark' ? 0.06 : 0.08})`,
    sidebarTriggerHoverBg: `rgba(${ink}, ${scheme === 'dark' ? 0.06 : 0.08})`,
    // No customer token for the active marker — keep DecisionPoint's orange.
    accent: '#F4770C',
  };
}

/**
 * Data-visualization series palette (Polaris "Extended" + "Secondary" ramps),
 * ordered for maximum distinguishability. Chart components read
 * `theme.palette.chart[i]`.
 */
const chart = [
  '#793176', // summer plum
  '#D04020', // terracotta
  '#FFB81C', // golden amber
  '#774B97', // muted violet
  '#F8AD6D', // warm apricot
  '#FFD67C', // soft amber
  '#003472', // deep steel
  '#028D8B', // harbor teal
  '#9BC6EB', // light cerulean
  '#4E6A82', // muted steel
] as const;

// Semantic ramps — mode-blind (the prototype keeps brand hues in both modes).
// `dark`/`light` shades come from the kit's `TONES` (kit-data.js), which are the
// values the prototype's chips/deltas actually render (text uses the `dark`
// shade in light mode). These intentionally differ from the older CSS token
// sheet for warning/error — the rendered kit is authoritative.
const primary = { main: '#004785', light: '#4A90D9', dark: '#003566', contrastText: '#FFFFFF' };
const secondary = { main: '#F4770C', light: '#F89B45', dark: '#C45E08', contrastText: '#FFFFFF' };
const success = { main: '#189A18', light: '#2BB12B', dark: '#0D6F0D' };
const error = { main: '#AB2020', light: '#D14545', dark: '#7A1717' };
const warning = { main: '#FFB81C', light: '#FFD25F', dark: '#9F6E0A' };
const info = { main: '#004785', light: '#4A90D9', dark: '#003566' };
const grey = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
};

declare module '@mui/material/styles' {
  interface Palette {
    brand: BrandPalette;
    chart: string[];
    /** Hairline for *internal* row separators (lighter than `divider`). */
    borderSubtle: string;
    /** Alternate paper surface (dialog/drawer footers, zebra fills). */
    paperAlt: string;
    /** Table header fill. */
    tableHeaderBg: string;
  }
  interface PaletteOptions {
    brand?: BrandPalette;
    chart?: string[];
    borderSubtle?: string;
    paperAlt?: string;
    tableHeaderBg?: string;
  }
}

/**
 * Builds the application theme, optionally branded for the effective customer.
 *
 * Called with no argument (or with an all-null/malformed `branding`) it returns
 * the untouched Polaris defaults, so the unbranded path is byte-identical to the
 * pre-branding theme. `app/BrandedThemeProvider` is the only production caller.
 */
export function createAppTheme(branding?: BrandingTokens): Theme {
  const primaryHex = normalizeHex(branding?.brandPrimaryColor);
  const headerHex = normalizeHex(branding?.brandHeaderColor);

  const effectivePrimary = primaryHex ? brandedPrimary(primaryHex) : primary;
  const effectiveBrandLight = headerHex ? brandedSidebar(headerHex, 'light') : brandLight;
  const effectiveBrandDark = headerHex ? brandedSidebar(headerHex, 'dark') : brandDark;

  return createTheme({
    cssVariables: { colorSchemeSelector: 'class' },
    colorSchemes: {
      light: {
        palette: {
          mode: 'light',
          primary: effectivePrimary,
          secondary,
          success,
          error,
          warning,
          info,
          grey,
          text: {
            primary: '#041D33', // Deep Navy
            secondary: '#435666', // Slate
            disabled: 'rgba(4, 29, 51, 0.38)',
          },
          background: {
            default: '#EDF0F2', // Soft Cool Grey
            paper: '#FFFFFF',
          },
          divider: '#DFE3E7', // Pale Blue Grey
          borderSubtle: 'rgba(4, 29, 51, 0.06)',
          paperAlt: '#F6F8FA',
          tableHeaderBg: 'rgba(4, 29, 51, 0.03)',
          brand: effectiveBrandLight,
          chart: [...chart],
        },
      },
      dark: {
        palette: {
          mode: 'dark',
          primary: effectivePrimary,
          secondary,
          success,
          error,
          warning,
          info,
          grey,
          text: {
            primary: 'rgba(255, 255, 255, 0.92)',
            secondary: 'rgba(255, 255, 255, 0.65)',
            disabled: 'rgba(255, 255, 255, 0.40)',
          },
          background: {
            default: '#0A1929', // navy-tinted, not neutral black
            paper: '#142639',
          },
          divider: 'rgba(255, 255, 255, 0.10)',
          borderSubtle: 'rgba(255, 255, 255, 0.06)',
          paperAlt: '#1A2E45',
          tableHeaderBg: 'rgba(255, 255, 255, 0.04)',
          brand: effectiveBrandDark,
          chart: [...chart],
        },
      },
    },
    typography: {
      fontFamily: FONT_BODY,
      fontSize: 15,
      h1: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: '60px', lineHeight: '70px' },
      h2: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '40px', lineHeight: '50px' },
      h3: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '26px', lineHeight: '34px' },
      h4: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '24px', lineHeight: '30px' },
      h5: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: '20px', lineHeight: '28px' },
      h6: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '18px', lineHeight: '27px' },
      subtitle1: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: '18px', lineHeight: '27px' },
      subtitle2: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: '15px', lineHeight: '24px' },
      body1: { fontFamily: FONT_BODY, fontWeight: 400, fontSize: '15px', lineHeight: '24px' },
      body2: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: '13px', lineHeight: '21px' },
      button: {
        fontFamily: FONT_UI,
        // Rendered prototype buttons (Button.jsx / SubmitButton) use weight 500,
        // not the 700 the older CSS token sheet lists. The kit is authoritative.
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
        textTransform: 'none',
      },
      caption: { fontFamily: FONT_BODY, fontWeight: 500, fontSize: '13px', lineHeight: '21px' },
      overline: {
        fontFamily: FONT_BODY,
        fontWeight: 600,
        fontSize: '13px',
        lineHeight: '21px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    shape: {
      // theme.shape.borderRadius is 8 (outlined inputs); component overrides
      // below drop buttons/cards/chips/dialogs to 4 per the token sheet.
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 4,
            whiteSpace: 'nowrap',
          },
          // Per-size padding + minHeight from the prototype Button.jsx sizes.
          sizeSmall: { padding: '4px 12px', minHeight: 32 },
          sizeMedium: { padding: '8px 16px', minHeight: 40 },
          sizeLarge: { padding: '12px 24px', minHeight: 48 },
        },
      },
      MuiCard: {
        styleOverrides: { root: { borderRadius: 4 } },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 4 } },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 4 } },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiAppBar: {
        defaultProps: { color: 'default', elevation: 0 },
      },
    },
  });
}

/**
 * The unbranded default theme. Used by tests, and as the pre-`whoami` theme by
 * {@link import('./app/BrandedThemeProvider')} so the app has a valid theme on
 * first paint (public/pre-auth routes never resolve a customer at all).
 */
export const theme = createAppTheme();
